import { useState, useRef, useCallback } from 'react';
import { Upload, Download, FileSpreadsheet, CheckCircle, AlertTriangle, X, Check } from 'lucide-react';
import { useStore, type Team } from '../store/useStore';
import { useVisibleStore } from '../hooks/useVisibleStore';
import * as XLSX from 'xlsx';

const TEAM_COLORS = [
  '#6366f1', '#ec4899', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#10b981', '#3b82f6',
  '#5f0061', '#004d4b', '#6b5900', '#0004fa', '#b30000', '#01686f', '#3d1500'
];

// Column name aliases for auto-mapping
const COLUMN_ALIASES: Record<string, string> = {
  name: 'name', nom: 'name', 'nom complet': 'name', 'full name': 'name', prénom: 'firstName',
  prenom: 'firstName', 'first name': 'firstName', 'firstname': 'firstName',
  nomdefamille: 'lastName', 'last name': 'lastName', 'lastname': 'lastName',
  email: 'email', 'e-mail': 'email', courriel: 'email', mail: 'email',
  phone: 'phone', téléphone: 'phone', tel: 'phone', telephone: 'phone', mobile: 'phone', portable: 'phone',
  role: 'role', rôle: 'role', 'rôle principal': 'role',
  category: 'category', catégorie: 'category', categorie: 'category',
  roles: 'roles', 'rôles personnalisés': 'roles', 'custom roles': 'roles',
};

function normalizeString(s: string): string {
  return (s || '').toString().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

function nameSimilarity(a: string, b: string): number {
  const na = normalizeString(a);
  const nb = normalizeString(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;

  // Simple Levenshtein-based ratio
  const matrix: number[][] = [];
  for (let i = 0; i <= na.length; i++) {
    matrix[i] = [i];
    for (let j = 1; j <= nb.length; j++) {
      if (i === 0) { matrix[i][j] = j; continue; }
      const cost = na[i - 1] === nb[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  const maxLen = Math.max(na.length, nb.length);
  return maxLen > 0 ? 1 - matrix[na.length][nb.length] / maxLen : 0;
}

interface ParsedRow {
  index: number;
  raw: Record<string, string>;
  mapped: {
    name?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    role?: string;
    category?: string;
    customFields?: Record<string, string>; // fieldId → value
  };
  status: 'new' | 'duplicate_email' | 'similar_name' | 'unknown_category' | 'skipped';
  existingTeam?: Team;
  similarity?: number;
}

export function ImportTeam() {
  const { addTeam, updateTeam, addCategory, currentOrgId, categories, customFieldDefinitions, setTeamCustomFieldValue, setTeamCustomFieldValues } = useStore();
  const { team: allTeam } = useVisibleStore();

  // Get org custom fields for template + import
  const orgCustomFields = customFieldDefinitions
    .filter(f => f.orgId === currentOrgId)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mergeName, setMergeName] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ created: number; updated: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [unknownCategories, setUnknownCategories] = useState<string[]>([]);
  const [categoryMapping, setCategoryMapping] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const orgCategories = categories.filter(c => c.orgId === currentOrgId);
  const orgTeam = allTeam.filter(s => s.orgId === currentOrgId && !s.isArtist);

  const parseFile = useCallback((f: File) => {
    setError(null);
    setResult(null);
    setRows([]);
    setSelectedRows(new Set());

    // Snapshot custom fields & categories from store to avoid stale closure in useCallback
    const currentOrgCustomFields = useStore.getState().customFieldDefinitions
      .filter(f => f.orgId === currentOrgId)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    const currentOrgCategories = useStore.getState().categories.filter(c => c.orgId === currentOrgId);

    const isCSV = f.name.toLowerCase().endsWith('.csv');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        let workbook;
        if (isCSV) {
          // Read CSV as UTF-8 text to preserve accents
          const text = e.target!.result as string;
          workbook = XLSX.read(text, { type: 'string', codepage: 65001 });
        } else {
          const data = new Uint8Array(e.target!.result as ArrayBuffer);
          workbook = XLSX.read(data, { type: 'array' });
        }
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1 });

        if (jsonData.length < 2) {
          setError('Le fichier est vide ou ne contient pas de données.');
          return;
        }

        const rawHeaders = (jsonData[0] as string[]).map(h => (h || '').toString().trim());
        setHeaders(rawHeaders);

        // Map column indices to known fields
        const colMap: Record<string, number> = {};
        rawHeaders.forEach((h, i) => {
          const norm = normalizeString(h);
          // Try exact normalized match against normalized alias keys
          for (const [alias, field] of Object.entries(COLUMN_ALIASES)) {
            if (norm === normalizeString(alias) && !colMap[field]) {
              colMap[field] = i;
              break;
            }
          }
        });

        // Also try partial matching for unmatched columns
        rawHeaders.forEach((h, i) => {
          const norm = normalizeString(h);
          const alreadyMapped = Object.values(colMap).includes(i);
          if (alreadyMapped) return;
          // Try contains match (normalized)
          for (const [alias, field] of Object.entries(COLUMN_ALIASES)) {
            const normAlias = normalizeString(alias);
            if (normAlias && norm.includes(normAlias) && !colMap[field]) {
              colMap[field] = i;
              break;
            }
          }
        });

        // Map custom field columns by label match
        const customFieldColMap: Record<string, number> = {}; // fieldId → column index
        currentOrgCustomFields.forEach(f => {
          rawHeaders.forEach((h, i) => {
            if (normalizeString(h) === normalizeString(f.label)) {
              customFieldColMap[f.id] = i;
            }
          });
        });

        const parsed: ParsedRow[] = [];
        for (let r = 1; r < jsonData.length; r++) {
          const row = jsonData[r] as string[];
          if (!row || row.every(c => !c)) continue; // skip empty rows

          const raw: Record<string, string> = {};
          rawHeaders.forEach((h, i) => { raw[h] = (row[i] || '').toString().trim(); });

          // Parse custom fields from matched columns
          const customFields: Record<string, string> = {};
          Object.entries(customFieldColMap).forEach(([fieldId, colIdx]) => {
            const val = (row[colIdx] || '').toString().trim();
            if (val) customFields[fieldId] = val;
          });

          const mapped = {
            name: colMap.name !== undefined ? String(row[colMap.name] || '').trim() : undefined,
            firstName: colMap.firstName !== undefined ? String(row[colMap.firstName] || '').trim() : undefined,
            lastName: colMap.lastName !== undefined ? String(row[colMap.lastName] || '').trim() : undefined,
            email: colMap.email !== undefined ? String(row[colMap.email] || '').trim().toLowerCase() : undefined,
            phone: colMap.phone !== undefined ? String(row[colMap.phone] || '').trim() : undefined,
            role: colMap.role !== undefined ? String(row[colMap.role] || '').trim() : undefined,
            category: colMap.category !== undefined ? String(row[colMap.category] || '').trim() : undefined,
            customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
          };

          // Resolve name: priority = name > firstName+lastName > first non-empty cell
          let resolvedName = mapped.name;
          if (!resolvedName && (mapped.firstName || mapped.lastName)) {
            resolvedName = [mapped.firstName, mapped.lastName].filter(Boolean).join(' ');
          }
          if (!resolvedName) {
            // Try first non-empty text cell that's not email/phone
            for (let c = 0; c < row.length; c++) {
              const val = (row[c] || '').trim();
              if (val && c !== colMap.email && c !== colMap.phone && !val.includes('@')) {
                resolvedName = val;
                break;
              }
            }
          }

          parsed.push({
            index: r,
            raw,
            mapped: { ...mapped, name: resolvedName || '' },
            status: 'new',
          });
        }

        // ── Detect duplicates ──
        // Email map against existing teams (cross-category)
        const emailMap = new Map<string, Team>();
        // Name map against existing teams (cross-category dedup by name)
        const nameNormMap = new Map<string, Team>();
        orgTeam.forEach(s => {
          if (s.email) emailMap.set(s.email.toLowerCase().trim(), s);
          if (s.name) nameNormMap.set(normalizeString(s.name), s);
        });

        // Within-file dedup: email and name
        const fileEmailMap = new Map<string, number>();
        const fileNameMap = new Map<string, number>();
        parsed.forEach((row) => {
          if (row.status !== 'new') return;
          const email = row.mapped.email;
          const name = row.mapped.name;
          // Within-file email dedup
          if (email && fileEmailMap.has(email)) {
            row.status = 'duplicate_email';
          } else if (email) {
            fileEmailMap.set(email, row.index);
          }
          // Within-file name dedup
          if (name && row.status === 'new') {
            const norm = normalizeString(name);
            if (fileNameMap.has(norm)) {
              row.status = 'duplicate_email';
            } else {
              fileNameMap.set(norm, row.index);
            }
          }
        });

        const selected = new Set<number>();
        parsed.forEach((row) => {
          const email = row.mapped.email;
          const name = row.mapped.name;

          // Email duplicate check (cross-category)
          if (email && emailMap.has(email) && row.status === 'new') {
            row.status = 'duplicate_email';
            row.existingTeam = emailMap.get(email);
          }

          // Name exact match check (cross-category dedup)
          if (name && row.status === 'new') {
            const nameNorm = normalizeString(name);
            if (nameNormMap.has(nameNorm)) {
              row.status = 'duplicate_email';
              row.existingTeam = nameNormMap.get(nameNorm);
            }
          }

          // Name similarity check (only if not already duplicate)
          if (name && row.status === 'new') {
            for (const s of orgTeam) {
              const sim = nameSimilarity(name, s.name);
              if (sim >= 0.85 && sim < 1) {
                // High similarity → treat as duplicate; medium → flag as similar
                row.status = sim >= 0.95 ? 'duplicate_email' : 'similar_name';
                row.similarity = sim;
                row.existingTeam = s;
                break;
              }
            }
          }

          // Auto-select only if new OR if duplicate with empty/missing fields to fill
          let shouldAutoSelect: boolean = row.status === 'new';
          if (row.status === 'duplicate_email' && row.existingTeam) {
            const s = row.existingTeam;
            const hasEmptyFields: boolean = !!(
              (!s.phone && row.mapped.phone) ||
              (!s.name || s.name === row.mapped.email) ||
              (!s.role && row.mapped.role) ||
              (row.mapped.customFields && Object.keys(row.mapped.customFields).length > 0)
            );
            shouldAutoSelect = hasEmptyFields;
          }

          // Unknown category detection
          if (row.mapped.category && row.status === 'new') {
            const catExists = currentOrgCategories.some(c =>
              normalizeString(c.name) === normalizeString(row.mapped.category || '')
            );
            if (!catExists) {
              row.status = 'unknown_category';
            }
          }

          if (shouldAutoSelect) selected.add(row.index);
        });

        // Collect unique unknown categories
        const unknownCats = [...new Set(
          parsed
            .filter(r => r.status === 'unknown_category')
            .map(r => r.mapped.category || '')
            .filter(Boolean)
        )];
        setUnknownCategories(unknownCats);

        setRows(parsed);
        setSelectedRows(selected);

        if (parsed.length === 0) {
          setError('Aucune donnée valide trouvée dans le fichier.');
        }
      } catch (err: any) {
        setError('Erreur lors de la lecture du fichier : ' + err.message);
        console.error('[ImportTeam] Parse error:', err);
      }
    };
    if (isCSV) {
      reader.readAsText(f, 'UTF-8');
    } else {
      reader.readAsArrayBuffer(f);
    }
  }, [orgTeam]);

  const toggleRow = (index: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    const allSelected = rows.every(r => selectedRows.has(r.index));
    if (allSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(rows.filter(r => {
        if (r.status === 'skipped') return false;
        if (r.status === 'duplicate_email' && r.existingTeam) {
          const s = r.existingTeam;
          return (!s.phone && r.mapped.phone) || (!s.name || s.name === r.mapped.email) || (!s.role && r.mapped.role) || (r.mapped.customFields && Object.keys(r.mapped.customFields).length > 0);
        }
        if (r.status === 'similar_name' || r.status === 'unknown_category') return false;
        return true;
      }).map(r => r.index)));
    }
  };

  // Apply name merge
  const mergedRows = mergeName
    ? rows.map(r => {
      if (r.mapped.firstName || r.mapped.lastName) {
        const merged = [r.mapped.firstName, r.mapped.lastName].filter(Boolean).join(' ');
        return { ...r, mapped: { ...r.mapped, name: merged || r.mapped.name } };
      }
      return r;
    })
    : rows;

  const handleImport = async () => {
    const toImport = mergedRows.filter(r => selectedRows.has(r.index) && r.status !== 'skipped');
    if (toImport.length === 0) return;

    setImporting(true);
    setProgress(0);
    setResult(null);
    let created = 0;
    let updated = 0;
    let skipped = 0;

    for (let i = 0; i < toImport.length; i++) {
      const row = toImport[i];
      setProgress(Math.round(((i + 1) / toImport.length) * 100));

      try {
        if (row.status === 'duplicate_email' && row.existingTeam) {
          // Merge: fill empty fields, merge roles (union)
          const updates: Partial<Team> = {};
          const s = row.existingTeam;
          if (!s.phone && row.mapped.phone) updates.phone = row.mapped.phone;
          if (!s.name || s.name === row.mapped.email) updates.name = row.mapped.name || s.name;
          // Merge roles: union existing roles + new role
          const existingRoles = s.roles || [];
          const newRole = row.mapped.role?.trim();
          const mergedRoles = newRole && !existingRoles.includes(newRole)
            ? [...existingRoles, newRole]
            : existingRoles;
          if (!s.role && row.mapped.role) updates.role = row.mapped.role;
          if (mergedRoles.length > existingRoles.length) updates.roles = mergedRoles;
          if (Object.keys(updates).length > 0) {
            updateTeam(s.id, updates);
          }
          // Save custom field values for existing team
          if (row.mapped.customFields && row.existingTeam) {
            const values = Object.entries(row.mapped.customFields).map(([fieldId, value]) => ({ fieldId, value }));
            setTeamCustomFieldValues(row.existingTeam.id, values);
            console.log('[ImportTeam] Custom fields saved for existing team:', row.existingTeam.id, values);
          }
          updated++;
        } else {
          // Find category by name, use per-category mapping, or fall back to first org category
          let categoryId = orgCategories[0]?.id || '';
          if (row.mapped.category) {
            // Check per-category mapping first (for unknown categories assigned to existing ones)
            const mappedId = categoryMapping[row.mapped.category];
            if (mappedId) {
              categoryId = mappedId;
            } else {
              const catMatch = orgCategories.find(c =>
                normalizeString(c.name) === normalizeString(row.mapped.category || '')
              );
              if (catMatch) categoryId = catMatch.id;
            }
          }

          // Build roles array from the single role field so addTeam can merge properly
          const importRoles: string[] = row.mapped.role?.trim() ? [row.mapped.role.trim()] : [];
          const color = TEAM_COLORS[Math.floor(Math.random() * TEAM_COLORS.length)];
          const newTeamId = addTeam({
            name: row.mapped.name || 'Sans nom',
            email: row.mapped.email || '',
            phone: row.mapped.phone || '',
            role: row.mapped.role || '',
            roles: importRoles,
            categoryId,
            color,
          });
          // Save custom field values for newly created team
          if (row.mapped.customFields && newTeamId) {
            const values = Object.entries(row.mapped.customFields).map(([fieldId, value]) => ({ fieldId, value }));
            setTeamCustomFieldValues(newTeamId, values);
            console.log('[ImportTeam] Custom fields saved for new team:', newTeamId, values);
          }
          created++;
        }
      } catch (err) {
        console.error('[ImportTeam] Error importing row:', row.index, err);
        skipped++;
      }
    }

    setResult({ created, updated, skipped });
    setImporting(false);
  };

  const resetImport = () => {
    setRows([]);
    setHeaders([]);
    setMergeName(false);
    setSelectedRows(new Set());
    setResult(null);
    setError(null);
    setProgress(0);
    setUnknownCategories([]);
    setCategoryMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => setDragOver(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.csv') || f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
      parseFile(f);
    } else {
      setError('Format non supporté. Utilisez un fichier .csv ou .xlsx');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) parseFile(f);
  };

  const downloadTemplate = () => {
    const baseHeaders = ['Nom complet', 'Email', 'Téléphone', 'Rôle principal', 'Catégorie'];
    const customFieldHeaders = orgCustomFields.map(f => f.label);
    const headers = [...baseHeaders, ...customFieldHeaders];
    const exampleRow = ['Jean Dupont', 'jean@example.com', '0612345678', 'Agent', 'Sécurité'];
    const customExample = orgCustomFields.map(f => f.fieldType === 'number' ? '0' : '');
    const template = [headers, [...exampleRow, ...customExample]];
    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team');
    XLSX.writeFile(wb, 'template_import_team.xlsx');
  };

  const hasFirstNameCol = headers.some(h => {
    const n = normalizeString(h);
    return n === 'firstname' || n === normalizeString('prénom');
  });
  const hasLastNameCol = headers.some(h => {
    const n = normalizeString(h);
    return n === 'lastname' || n === normalizeString('nomdefamille');
  });
  const showMergeOption = hasFirstNameCol && hasLastNameCol;

  const newCount = mergedRows.filter(r => r.status === 'new' && selectedRows.has(r.index)).length;
  const dupCount = mergedRows.filter(r => r.status === 'duplicate_email' && selectedRows.has(r.index)).length;
  const simCount = mergedRows.filter(r => r.status === 'similar_name' && selectedRows.has(r.index)).length;
  const skippedCount = mergedRows.filter(r => !selectedRows.has(r.index)).length;

  return (
    <div className="flex flex-col flex-1 min-h-0 relative z-10">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0A0B0E]/95 backdrop-blur-sm border-b border-[#2D3139]/50 px-5 py-3.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
              <Upload className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Import Team</h2>
              <p className="text-xs text-slate-400">
                {rows.length > 0
                  ? `${rows.length} lignes détectées — ${newCount} nouveaux, ${dupCount} doublons`
                  : 'Importez un fichier CSV ou Excel (.xlsx)'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-xs font-medium transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Template
            </button>
            {rows.length > 0 && (
              <button
                onClick={resetImport}
                className="flex items-center gap-1.5 px-3 py-2 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-xs font-medium transition-colors"
              >
                <X className="w-3.5 h-3.5" />
                Réinitialiser
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-5">
        {/* Error */}
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="mb-4 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm px-4 py-3 rounded-xl">
            <div className="flex items-center gap-2 font-semibold mb-1">
              <CheckCircle className="w-4 h-4" />
              Import terminé !
            </div>
            <p>{result.created} créé(s), {result.updated} mis à jour, {result.skipped} ignoré(s)</p>
          </div>
        )}

        {rows.length === 0 ? (
          /* Upload Zone */
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
              dragOver
                ? 'border-emerald-500 bg-emerald-500/5'
                : 'border-[#2D3139] hover:border-slate-500 bg-[#14161B]/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={handleFileSelect}
            />
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                <FileSpreadsheet className="w-8 h-8 text-emerald-400" />
              </div>
              <div>
                <p className="text-white font-medium">Glissez-déposez votre fichier ici</p>
                <p className="text-slate-500 text-sm mt-1">ou cliquez pour parcourir</p>
              </div>
              <p className="text-slate-600 text-xs">Formats supportés : .csv, .xlsx</p>
            </div>
          </div>
        ) : (
          <>
            {/* Merge option */}
            {showMergeOption && (
              <label className="flex items-center gap-3 p-3 mb-4 bg-[#14161B]/80 border border-[#2D3139] rounded-xl cursor-pointer hover:border-slate-600 transition-all max-w-sm">
                <input
                  type="checkbox"
                  checked={mergeName}
                  onChange={e => setMergeName(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500/20"
                />
                <div>
                  <p className="text-sm text-white">Fusionner Prénom + Nom</p>
                  <p className="text-xs text-slate-500">Combiner les colonnes Prénom et Nom en Nom complet</p>
                </div>
              </label>
            )}

            {/* Column mapping info */}
            <div className="mb-4 flex flex-wrap gap-2">
              {headers.map(h => {
                const norm = normalizeString(h);
                // Find mapped field by normalizing alias keys too
                let mapped: string | null = null;
                for (const [alias, field] of Object.entries(COLUMN_ALIASES)) {
                  if (norm === normalizeString(alias)) { mapped = field; break; }
                }
                // Also check custom fields
                if (!mapped) {
                  const cf = orgCustomFields.find(f => norm === normalizeString(f.label));
                  if (cf) mapped = cf.label;
                }
                return (
                  <span key={h} className={`text-[10px] px-2 py-1 rounded-full border ${
                    mapped
                      ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                      : 'bg-slate-800/50 text-slate-500 border-slate-700'
                  }`}>
                    {h} {mapped ? `→ ${mapped}` : '(ignoré)'}
                  </span>
                );
              })}
            </div>

            {/* Unknown categories warning — per-category mapping */}
            {unknownCategories.length > 0 && (
              <div className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-300 font-semibold text-sm mb-1">
                      {unknownCategories.length} catégorie{unknownCategories.length > 1 ? 's' : ''} introuvable{unknownCategories.length > 1 ? 's' : ''}
                    </p>
                    <p className="text-amber-400/80 text-xs">
                      Pour chaque catégorie inconnue, choisissez de la créer ou de l'assigner à une catégorie existante.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  {unknownCategories.map(catName => {
                    const rowCount = rows.filter(r => r.mapped.category === catName).length;
                    return (
                      <div key={catName} className="flex items-center gap-3 bg-[#0A0B0E]/50 rounded-lg p-3">
                        <span className="text-white text-sm font-medium min-w-[120px] truncate" title={catName}>
                          {catName}
                        </span>
                        <span className="text-slate-500 text-[10px] whitespace-nowrap">{rowCount} ligne{rowCount > 1 ? 's' : ''}</span>
                        <div className="flex items-center gap-2 flex-1 justify-end">
                          <button
                            onClick={() => {
                              addCategory({ name: catName, isGeneral: false });
                              // Clear this category from unknown list
                              setUnknownCategories(prev => prev.filter(c => c !== catName));
                              // Re-parse to pick up the new category
                              if (fileInputRef.current?.files?.[0]) {
                                parseFile(fileInputRef.current.files[0]);
                              }
                            }}
                            className="px-3 py-1.5 bg-emerald-500/20 hover:bg-emerald-500/30 border border-emerald-500/30 text-emerald-400 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
                          >
                            + Créer
                          </button>
                          <span className="text-slate-600 text-xs">ou</span>
                          <select
                            value={categoryMapping[catName] || ''}
                            onChange={e => setCategoryMapping(prev => ({ ...prev, [catName]: e.target.value }))}
                            className="bg-[#0A0B0E] border border-[#2D3139] rounded-lg px-3 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500 appearance-none min-w-[140px]"
                          >
                            <option value="">Assigner à...</option>
                            {orgCategories.map(c => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Select all */}
            <div className="flex items-center gap-3 mb-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && rows.every(r => selectedRows.has(r.index))}
                  onChange={toggleAll}
                  className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500/20"
                />
                <span className="text-xs text-slate-400">Tout sélectionner</span>
              </label>
              <span className="text-xs text-slate-500">
                ({newCount} nouveaux, {dupCount} doublons, {simCount} similaires{unknownCategories.length > 0 ? `, ${unknownCategories.length} cat. inconnue` : ''}{skippedCount > 0 ? `, ${skippedCount} ignorés` : ''})
              </span>
            </div>

            {/* Preview table */}
            {(() => {
              const mappedCustomFieldIds = [...new Set(
                rows.flatMap(r => Object.keys(r.mapped.customFields || {}))
              )];
              const mappedCustomFields = mappedCustomFieldIds
                .map(id => orgCustomFields.find(f => f.id === id))
                .filter(Boolean) as typeof orgCustomFields;

              return (
              <div className="bg-[#14161B]/80 border border-[#2D3139] rounded-xl overflow-hidden mb-4">
                <div className="overflow-x-auto max-h-[50vh] custom-scrollbar">
                  <table className="w-full text-xs">
                    <thead className="bg-[#0A0B0E] sticky top-0 z-10">
                      <tr>
                        <th className="p-3 text-left w-10">#</th>
                        <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Nom</th>
                        <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Email</th>
                        <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Téléphone</th>
                        <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Rôle</th>
                        <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Catégorie</th>
                        {mappedCustomFields.map(f => (
                          <th key={f.id} className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">{f.label}</th>
                        ))}
                        <th className="p-3 text-left text-slate-400 font-semibold uppercase tracking-wider">Statut</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#2D3139]/50">
                      {mergedRows.map((row) => {
                        const isSel = selectedRows.has(row.index);
                        return (
                          <tr
                            key={row.index}
                            className={`transition-colors ${isSel ? 'bg-emerald-500/5' : 'opacity-50'} hover:bg-[#1A1D23]/50`}
                          >
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={isSel}
                                onChange={() => toggleRow(row.index)}
                                className="w-4 h-4 rounded border-slate-700 bg-slate-800 text-emerald-500 focus:ring-emerald-500/20"
                              />
                            </td>
                            <td className="p-3 text-white font-medium">
                              {row.mapped.name || <span className="text-slate-600">—</span>}
                            </td>
                            <td className="p-3 text-slate-300">
                              {row.mapped.email || <span className="text-slate-600">—</span>}
                            </td>
                            <td className="p-3 text-slate-300">
                              {row.mapped.phone || <span className="text-slate-600">—</span>}
                            </td>
                            <td className="p-3 text-slate-300">
                              {row.mapped.role || <span className="text-slate-600">—</span>}
                            </td>
                            <td className="p-3 text-slate-300">
                              {row.mapped.category || <span className="text-slate-600">—</span>}
                            </td>
                            {mappedCustomFields.map(f => (
                              <td key={f.id} className="p-3 text-slate-300">
                                {row.mapped.customFields?.[f.id] || <span className="text-slate-600">—</span>}
                              </td>
                            ))}
                            <td className="p-3">
                              {row.status === 'new' && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                  Nouveau
                                </span>
                              )}
                              {row.status === 'duplicate_email' && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20" title={`Existant: ${row.existingTeam?.name}`}>
                                  ✏️ Doublon email
                                </span>
                              )}
                              {row.status === 'similar_name' && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20" title={`Similaire à: ${row.existingTeam?.name} (${Math.round((row.similarity || 0) * 100)}%)`}>
                                  ⚠️ Nom similaire
                                </span>
                              )}
                              {row.status === 'unknown_category' && (
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                                  ❓ Cat. inconnue
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              );
            })()}

            {/* Import button */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleImport}
                disabled={importing || newCount + dupCount === 0}
                className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20 transition-all"
              >
                {importing ? (
                  <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Importation... ({progress}%)</>
                ) : (
                  <><Check className="w-4 h-4" /> Importer {newCount + dupCount} team{newCount + dupCount > 1 ? 's' : ''}</>
                )}
              </button>
              {result && (
                <button
                  onClick={resetImport}
                  className="px-4 py-2.5 bg-[#1A1D23] border border-[#2D3139] text-slate-300 hover:text-white rounded-xl text-sm font-medium transition-colors"
                >
                  Nouvel import
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
